import { ServiceHandler } from '../../../utils/api.utils';
import type {
  MooTask,
  MooTaskCreate,
  MooTaskTestInitiated,
  MooTaskUpdate,
  MooTaskRun,
  TaskListResponse,
  RunListResponse,
  TriggerCatalogEntry,
  OutputCatalogEntry,
  SlackChannel,
  TaskRunBackgroundProgress,
} from '../types/moo-tasks.types';

const BASE = '/api/chat-agent/v1';

export const listMooTasks = ServiceHandler<unknown, TaskListResponse>({
  url: `${BASE}/moo-tasks`,
});

/** Largest `limit` `GET /moo-tasks` accepts. */
export const MOO_TASKS_MAX_LIMIT = 200;

export const getMooTask = ServiceHandler<unknown, MooTask>({
  url: `${BASE}/moo-tasks/:taskId`,
});

export const createMooTask = ServiceHandler<MooTaskCreate, MooTask>({
  url: `${BASE}/moo-tasks`,
  method: 'POST',
});

export const updateMooTask = ServiceHandler<MooTaskUpdate, MooTask>({
  url: `${BASE}/moo-tasks/:taskId`,
  method: 'PATCH',
});

export const deleteMooTask = ServiceHandler<
  unknown,
  { id: string; status: string }
>({
  url: `${BASE}/moo-tasks/:taskId`,
  method: 'DELETE',
});

export const activateMooTask = ServiceHandler<unknown, MooTask>({
  url: `${BASE}/moo-tasks/:taskId/activate`,
  method: 'POST',
});

export const stopMooTask = ServiceHandler<unknown, MooTask>({
  url: `${BASE}/moo-tasks/:taskId/stop`,
  method: 'POST',
});

/**
 * Stop this task's proactive nudges for the CURRENT user (OMNI-757). The muting
 * user is taken from the JWT server-side — you can only ever mute for yourself,
 * and this never changes the task for anyone else.
 */
export const muteMooTask = ServiceHandler<unknown, { muted: boolean }>({
  url: `${BASE}/moo-tasks/:taskId/mute`,
  method: 'POST',
});

/** Resume this task's proactive nudges for the current user. */
export const unmuteMooTask = ServiceHandler<unknown, { muted: boolean }>({
  url: `${BASE}/moo-tasks/:taskId/mute`,
  method: 'DELETE',
});

export const runMooTask = ServiceHandler<
  { rerun_from_run_id?: string },
  MooTaskRun
>({
  url: `${BASE}/moo-tasks/:taskId/run`,
  method: 'POST',
});

// Test is a dedicated dry run: POST /test returns 202 + {run_id} immediately;
// poll GET /moo-tasks/:taskId/runs/:runId until status leaves 'running'.
// Delivers to output channels only when deliver_outputs: true (which then also
// bumps run counters); otherwise channels are skipped and counters untouched.
export const testMooTask = ServiceHandler<
  { deliver_outputs: boolean; entity_type?: string; entity_id?: string },
  MooTaskTestInitiated
>({
  url: `${BASE}/moo-tasks/:taskId/test`,
  method: 'POST',
});

export const listTaskRunsForTask = ServiceHandler<unknown, RunListResponse>({
  url: `${BASE}/moo-tasks/:taskId/runs`,
});

export const getTaskRun = ServiceHandler<unknown, MooTaskRun>({
  url: `${BASE}/moo-tasks/:taskId/runs/:runId`,
});

// Per-child unit/progress detail for a run parked on background async
// children (status 'background_running'). Same unit shape the interactive
// AsyncTaskProgress card polls, task-scoped so no conversation id is needed.
export const getTaskRunBackgroundProgress = ServiceHandler<
  unknown,
  TaskRunBackgroundProgress
>({
  url: `${BASE}/moo-tasks/:taskId/runs/:runId/background`,
});

// Spawns a NEW task run (triggered_by 'retry') preloaded with this run's
// failed background units — prep skipped, original row untouched.
export const retryTaskRunFailedUnits = ServiceHandler<unknown, MooTaskRun>({
  url: `${BASE}/moo-tasks/:taskId/runs/:runId/retry-failed`,
  method: 'POST',
});

// Cancels a run parked on background children: stops every child shard
// group and finalizes the run failed ("cancelled by user") — no delivery.
// 409 when the run is already finalizing or a retry is still spawning.
// COMPANION BACKEND: chat-agent AUTO-465-cancel-run — 404s until it
// deploys (the drawer maps that to a "not available yet" notification).
export const cancelTaskRun = ServiceHandler<unknown, MooTaskRun>({
  url: `${BASE}/moo-tasks/:taskId/runs/:runId/cancel`,
  method: 'POST',
});

export const listAllTaskRuns = ServiceHandler<unknown, RunListResponse>({
  url: `${BASE}/task-runs`,
});

export const listTriggerCatalog = ServiceHandler<
  unknown,
  { triggers: TriggerCatalogEntry[]; total: number }
>({
  url: `${BASE}/trigger-catalog`,
});

export const listOutputCatalog = ServiceHandler<
  unknown,
  { outputs: OutputCatalogEntry[]; total: number }
>({
  url: `${BASE}/output-catalog`,
});

// Type-ahead source for the Slack channel picker. Pass a query via axios params:
// Lists the Slack channels the bot can see; the picker fetches once and filters
// client-side (Slack has no channel-name search API for a bot token).
export const listSlackChannels = ServiceHandler<
  unknown,
  { channels: SlackChannel[]; message?: string; building?: boolean }
>({
  url: `${BASE}/integrations/slack/channels`,
});
